import { useState, useEffect, useCallback } from 'react';
import {
  BarChart3, Users, MessageSquare, Zap, RefreshCw, Loader2,
  TrendingUp, Filter, Info, Share2, Search, FileText, LogIn,
  Star, Trash2, Activity, ThumbsUp, ThumbsDown,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, PieChart, Pie, Cell,
} from 'recharts';
import { APP_CONFIG } from '../config/appConfig';
import { authFetch } from '../config/authConfig';

interface Overview {
  total_sesiones: number;
  total_consultas: number;
  usuarios_activos: number;
  promedio_consultas_sesion: number;
  tokens_entrada: number;
  tokens_salida: number;
  tokens_total: number;
}

interface SessionUser {
  id: string;
  nombre: string;
  email: string;
  departamento: string;
  sesiones: number;
  total_mensajes: number;
}

interface TokenUser {
  id: string;
  nombre: string;
  email: string;
  tokens_entrada: number;
  tokens_salida: number;
  tokens_total: number;
}

interface MethodData { method: string; total: number; porcentaje: number; }
interface SharingStats {
  total_compartidos: number;
  conversaciones_compartidas: number;
  usuarios_compartiendo: number;
  por_dia: { date: string; compartidos: number }[];
}
interface TokenDay { date: string; entrada: number; salida: number; total: number; }
interface HourlyData { hour: string; consultas: number; }
interface ActivityItem { id: string; user: string; type: string; description: string; timestamp: string; }
interface RoleSummary { id: number; nombre: string; }

const API_BASE = APP_CONFIG.BACKEND_URL;

const PERIOD_OPTIONS = [
  { value: 7, label: '7 días' },
  { value: 30, label: '30 días' },
  { value: 90, label: '90 días' },
];

const METHOD_COLORS: Record<string, string> = {
  SQL: '#4F46E5',
  RAG: '#059669',
  'Híbrido': '#D97706',
  Otro: '#94A3B8',
};

const ACTIVITY_ICONS: Record<string, React.ElementType> = {
  query: Search,
  share: Share2,
  export: FileText,
  download: FileText,
  login: LogIn,
  logout: LogIn,
  favorite: Star,
  new_conversation: MessageSquare,
  delete_conversation: Trash2,
  rename_conversation: Activity,
};

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

function formatTime(ts: string) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Ayer';
  return `Hace ${days}d`;
}

function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <div className="mb-4">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <p className="text-[11px] text-gray-400 mt-0.5">{description}</p>
    </div>
  );
}

export default function AdminDashboardPage() {
  const [days, setDays] = useState(30);
  const [filterUserId, setFilterUserId] = useState('');
  const [filterRoleId, setFilterRoleId] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [overview, setOverview] = useState<Overview | null>(null);
  const [sessionsPerUser, setSessionsPerUser] = useState<SessionUser[]>([]);
  const [tokensPerUser, setTokensPerUser] = useState<TokenUser[]>([]);
  const [queriesPerDay, setQueriesPerDay] = useState<any[]>([]);
  const [sessionsPerDay, setSessionsPerDay] = useState<any[]>([]);
  const [hourlyData, setHourlyData] = useState<HourlyData[]>([]);
  const [methodData, setMethodData] = useState<MethodData[]>([]);
  const [sharingStats, setSharingStats] = useState<SharingStats | null>(null);
  const [tokensPerDay, setTokensPerDay] = useState<TokenDay[]>([]);
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [ratingSummary, setRatingSummary] = useState<any>(null);
  const [allUsers, setAllUsers] = useState<{ id: string; nombre: string }[]>([]);
  const [allRoles, setAllRoles] = useState<RoleSummary[]>([]);

  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    p.set('days', days.toString());
    if (filterUserId) p.set('user_id', filterUserId);
    if (filterRoleId) p.set('role_id', filterRoleId.toString());
    return p.toString();
  }, [days, filterUserId, filterRoleId]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const qs = buildParams();
    const roleFilter = filterRoleId ? `&role_id=${filterRoleId}` : '';
    try {
      const results = await Promise.all([
        authFetch(`${API_BASE}/api/v1/admin/metrics/overview?${qs}`),
        authFetch(`${API_BASE}/api/v1/admin/metrics/sessions-per-user?days=${days}${roleFilter}`),
        authFetch(`${API_BASE}/api/v1/admin/metrics/tokens-per-user?days=${days}${roleFilter}`),
        authFetch(`${API_BASE}/api/v1/admin/metrics/queries-per-day?${qs}`),
        authFetch(`${API_BASE}/api/v1/admin/metrics/sessions-per-day?${qs}`),
        authFetch(`${API_BASE}/api/v1/admin/metrics/queries-per-hour?${qs}`),
        authFetch(`${API_BASE}/api/v1/admin/metrics/method-distribution?${qs}`),
        authFetch(`${API_BASE}/api/v1/admin/metrics/sharing-stats?days=${days}`),
        authFetch(`${API_BASE}/api/v1/admin/metrics/tokens-per-day?${qs}`),
        authFetch(`${API_BASE}/api/v1/admin/metrics/recent-activity?limit=20`),
        authFetch(`${API_BASE}/api/v1/ratings/admin/summary?days=${days}`),
        authFetch(`${API_BASE}/api/v1/users`),
        authFetch(`${API_BASE}/api/v1/roles`),
      ]);

      const [ovR, spuR, tpuR, qpdR, spdR, hR, mdR, ssR, tpdR, raR, rtgR, uR, rR] = results;

      if (ovR.ok) setOverview(await ovR.json());
      if (spuR.ok) setSessionsPerUser(await spuR.json());
      if (tpuR.ok) setTokensPerUser(await tpuR.json());
      if (qpdR.ok) setQueriesPerDay(await qpdR.json());
      if (spdR.ok) setSessionsPerDay(await spdR.json());
      if (hR.ok) setHourlyData(await hR.json());
      if (mdR.ok) setMethodData(await mdR.json());
      if (ssR.ok) setSharingStats(await ssR.json());
      if (tpdR.ok) setTokensPerDay(await tpdR.json());
      if (raR.ok) setRecentActivity(await raR.json());
      if (rtgR.ok) setRatingSummary(await rtgR.json());
      if (uR.ok) {
        const u = await uR.json();
        setAllUsers(u.map((x: any) => ({ id: x.id, nombre: `${x.nombre} ${x.apellido || ''}`.trim() })));
      }
      if (rR.ok) {
        const r = await rR.json();
        setAllRoles(r.map((x: any) => ({ id: x.id, nombre: x.nombre })));
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [buildParams, days, filterRoleId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const hasFilters = !!filterUserId || !!filterRoleId;

  const metricCards = overview ? [
    { label: 'Sesiones Totales', value: formatNumber(overview.total_sesiones), icon: MessageSquare, color: '#4F46E5', desc: 'Conversaciones creadas' },
    { label: 'Consultas Totales', value: formatNumber(overview.total_consultas), icon: TrendingUp, color: '#0891B2', desc: 'Mensajes enviados al agente' },
    { label: 'Usuarios Activos', value: overview.usuarios_activos.toString(), icon: Users, color: '#059669', desc: 'Usuarios únicos con actividad' },
    { label: 'Consultas / Sesión', value: overview.promedio_consultas_sesion.toString(), icon: BarChart3, color: '#D97706', desc: 'Promedio de mensajes por sesión' },
    { label: 'Tokens Entrada', value: formatNumber(overview.tokens_entrada), icon: Zap, color: '#7C3AED', desc: 'Tokens consumidos en prompts' },
    { label: 'Tokens Salida', value: formatNumber(overview.tokens_salida), icon: Zap, color: '#DB2777', desc: 'Tokens generados en respuestas' },
  ] : [];

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Panel Administrativo</h1>
          <p className="text-sm text-gray-500 mt-1">Visión global del uso de la plataforma por todos los usuarios.</p>
        </div>
        <div className="flex items-center gap-2">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setDays(opt.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                days === opt.value ? 'text-white' : 'text-gray-600 bg-white border border-gray-200 hover:bg-gray-50'
              }`}
              style={days === opt.value ? { backgroundColor: '#1B2A4A' } : {}}
            >
              {opt.label}
            </button>
          ))}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg border transition-colors ${showFilters ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}
            title="Filtros avanzados"
          >
            <Filter className="w-4 h-4" />
          </button>
          <button
            onClick={fetchData}
            className="p-2 rounded-lg bg-white border border-gray-200 text-gray-500 hover:bg-gray-50"
            title="Refrescar"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-100 rounded-xl">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-800">Métricas globales de la plataforma</p>
          <p className="text-xs text-blue-600 mt-0.5">
            Este panel muestra datos agregados de <strong>todos los usuarios</strong> del sistema.
            Usa los filtros de periodo, usuario o rol para segmentar la información.
            Las métricas se calculan en tiempo real desde la base de datos.
          </p>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="p-4 bg-white rounded-xl border border-gray-100" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <p className="text-xs text-gray-500 mb-3">Filtra todas las métricas y gráficas por un usuario o rol específico.</p>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Usuario</label>
              <select
                value={filterUserId}
                onChange={e => setFilterUserId(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': '#EDAA00' } as React.CSSProperties}
              >
                <option value="">Todos los usuarios</option>
                {allUsers.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Rol</label>
              <select
                value={filterRoleId}
                onChange={e => setFilterRoleId(e.target.value ? Number(e.target.value) : '')}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': '#EDAA00' } as React.CSSProperties}
              >
                <option value="">Todos los roles</option>
                {allRoles.map(r => <option key={r.id} value={r.id}>{r.nombre}</option>)}
              </select>
            </div>
            <button
              onClick={() => { setFilterUserId(''); setFilterRoleId(''); }}
              className="px-3 py-2 text-xs font-medium text-gray-500 hover:text-gray-700"
            >
              Limpiar filtros
            </button>
          </div>
          {hasFilters && (
            <p className="text-[11px] text-amber-600 mt-2 font-medium">
              Filtros activos — los datos mostrados corresponden solo a la selección aplicada.
            </p>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : (
        <>
          {/* Metric Cards */}
          <div>
            <SectionHeader
              title="Resumen general"
              description={`Indicadores clave de los últimos ${days} días. Incluye sesiones, consultas, usuarios activos y consumo de tokens del modelo de IA.`}
            />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {metricCards.map((card, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${card.color}15` }}>
                      <card.icon className="w-4 h-4" style={{ color: card.color }} />
                    </div>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{card.value}</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">{card.label}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Charts row 1: Consultas/día + Sesiones/día */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-100 p-5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <SectionHeader
                title="Consultas por día"
                description="Mensajes enviados al agente cada día. Identifica picos de demanda y patrones de uso."
              />
              {queriesPerDay.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={queriesPerDay}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94A3B8' }} tickLine={false} axisLine={false} interval={Math.max(0, Math.floor(queriesPerDay.length / 8))} />
                    <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '12px' }} cursor={{ fill: 'rgba(79, 70, 229, 0.06)' }} />
                    <Bar dataKey="consultas" fill="#4F46E5" radius={[4, 4, 0, 0]} name="Consultas" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-gray-400 text-center py-12">Sin datos para el periodo seleccionado</p>
              )}
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <SectionHeader
                title="Sesiones por día"
                description="Conversaciones nuevas iniciadas cada día. Refleja la frecuencia con la que los usuarios recurren al agente."
              />
              {sessionsPerDay.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={sessionsPerDay}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94A3B8' }} tickLine={false} axisLine={false} interval={Math.max(0, Math.floor(sessionsPerDay.length / 8))} />
                    <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '12px' }} />
                    <Line type="monotone" dataKey="sesiones" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} name="Sesiones" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-gray-400 text-center py-12">Sin datos para el periodo seleccionado</p>
              )}
            </div>
          </div>

          {/* Charts row 2: Uso por hora + Método de respuesta */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-100 p-5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <SectionHeader
                title="Distribución por hora del día"
                description="Horas en las que se concentra el uso del agente. Útil para planear mantenimiento y detectar uso fuera de horario."
              />
              {hourlyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={hourlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#94A3B8' }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '12px' }} cursor={{ fill: 'rgba(27, 42, 74, 0.06)' }} />
                    <Bar dataKey="consultas" fill="#1B2A4A" radius={[4, 4, 0, 0]} name="Consultas" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-gray-400 text-center py-12">Sin datos</p>
              )}
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <SectionHeader
                title="Método de respuesta del agente"
                description="Distribución de respuestas por tipo: SQL (consulta directa a datos), RAG (búsqueda semántica) o Híbrido. Indica qué enfoque predomina."
              />
              {methodData.length > 0 ? (
                <div className="flex items-center gap-6">
                  <ResponsiveContainer width="55%" height={250}>
                    <PieChart>
                      <Pie
                        data={methodData}
                        dataKey="total"
                        nameKey="method"
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={3}
                        strokeWidth={0}
                      >
                        {methodData.map((entry) => (
                          <Cell key={entry.method} fill={METHOD_COLORS[entry.method] || '#94A3B8'} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '12px' }}
                        formatter={(value: number, name: string) => [`${value} respuestas`, name]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-3">
                    {methodData.map(m => (
                      <div key={m.method} className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: METHOD_COLORS[m.method] || '#94A3B8' }} />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-700">{m.method}</span>
                            <span className="text-xs font-bold text-gray-900">{m.porcentaje}%</span>
                          </div>
                          <p className="text-[10px] text-gray-400">{m.total} respuestas</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-12">Sin datos de métodos</p>
              )}
            </div>
          </div>

          {/* Tokens trend */}
          <div className="bg-white rounded-xl border border-gray-100 p-5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <SectionHeader
              title="Tendencia de consumo de tokens"
              description="Tokens de entrada (prompts) y salida (respuestas) consumidos por día. Monitorea el costo del modelo de IA y detecta picos inusuales."
            />
            {tokensPerDay.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={tokensPerDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94A3B8' }} tickLine={false} axisLine={false} interval={Math.max(0, Math.floor(tokensPerDay.length / 8))} />
                  <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} tickLine={false} axisLine={false} tickFormatter={(v) => formatNumber(v)} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '12px' }} formatter={(v: number) => formatNumber(v)} />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Line type="monotone" dataKey="entrada" stroke="#7C3AED" strokeWidth={2} dot={false} name="Tokens entrada" />
                  <Line type="monotone" dataKey="salida" stroke="#DB2777" strokeWidth={2} dot={false} name="Tokens salida" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-gray-400 text-center py-12">Sin datos de tokens</p>
            )}
          </div>

          {/* Satisfaction / Ratings */}
          {ratingSummary && (
            <div className="bg-white rounded-xl border border-gray-100 p-5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <SectionHeader
                title="Satisfacción de usuarios"
                description="Calificaciones de sesiones (1-5 estrellas) y valoraciones de respuestas individuales (positiva/negativa). Mide la percepción del usuario sobre la calidad del agente."
              />
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                <div className="p-3 bg-yellow-50 rounded-xl text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    <p className="text-xl font-bold text-yellow-700">{ratingSummary.sesiones.promedio || '—'}</p>
                  </div>
                  <p className="text-[11px] text-yellow-600">Promedio sesión (1-5)</p>
                  <p className="text-[10px] text-yellow-500 mt-0.5">{ratingSummary.sesiones.total_calificaciones} calificaciones</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-xl text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <ThumbsUp className="w-4 h-4 text-emerald-500" />
                    <p className="text-xl font-bold text-emerald-700">{ratingSummary.respuestas.positivas}</p>
                  </div>
                  <p className="text-[11px] text-emerald-600">Respuestas positivas</p>
                </div>
                <div className="p-3 bg-red-50 rounded-xl text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <ThumbsDown className="w-4 h-4 text-red-400" />
                    <p className="text-xl font-bold text-red-700">{ratingSummary.respuestas.negativas}</p>
                  </div>
                  <p className="text-[11px] text-red-600">Respuestas negativas</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-xl text-center">
                  <p className="text-xl font-bold text-blue-700">{ratingSummary.respuestas.tasa_positiva}%</p>
                  <p className="text-[11px] text-blue-600">Tasa de satisfacción</p>
                  <p className="text-[10px] text-blue-500 mt-0.5">{ratingSummary.respuestas.total} valoraciones</p>
                </div>
              </div>

              {/* Stars distribution */}
              {ratingSummary.sesiones.distribucion.length > 0 && (
                <div className="mb-5">
                  <p className="text-xs font-medium text-gray-600 mb-2">Distribución de calificaciones de sesión</p>
                  <div className="space-y-1.5">
                    {[5,4,3,2,1].map(score => {
                      const item = ratingSummary.sesiones.distribucion.find((d: any) => d.score === score);
                      const count = item?.total || 0;
                      const maxCount = Math.max(...ratingSummary.sesiones.distribucion.map((d: any) => d.total), 1);
                      const pct = (count / maxCount) * 100;
                      return (
                        <div key={score} className="flex items-center gap-2">
                          <div className="flex items-center gap-0.5 w-16 justify-end">
                            <span className="text-xs text-gray-500">{score}</span>
                            <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                          </div>
                          <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full bg-yellow-400 transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs text-gray-500 w-8 text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Recent comments */}
              {ratingSummary.comentarios_recientes?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">Comentarios recientes de usuarios</p>
                  <div className="space-y-2 max-h-[240px] overflow-y-auto">
                    {ratingSummary.comentarios_recientes.map((c: any, i: number) => (
                      <div key={i} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex gap-0.5">
                            {[1,2,3,4,5].map(s => (
                              <Star key={s} className={`w-3 h-3 ${s <= c.score ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`} />
                            ))}
                          </div>
                          <span className="text-[10px] text-gray-400">{c.titulo}</span>
                        </div>
                        <p className="text-xs text-gray-700 italic">"{c.comentario}"</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sharing stats */}
          {sharingStats && (
            <div className="bg-white rounded-xl border border-gray-100 p-5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <SectionHeader
                title="Análisis compartidos"
                description="Mide la colaboración en el equipo. Si la actividad es baja, puede ser necesario incentivar el uso de la funcionalidad de compartir."
              />
              <div className="grid grid-cols-3 gap-4 mb-5">
                <div className="p-3 bg-indigo-50 rounded-xl text-center">
                  <p className="text-xl font-bold text-indigo-700">{sharingStats.total_compartidos}</p>
                  <p className="text-[11px] text-indigo-500 mt-0.5">Veces compartido</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-xl text-center">
                  <p className="text-xl font-bold text-emerald-700">{sharingStats.conversaciones_compartidas}</p>
                  <p className="text-[11px] text-emerald-500 mt-0.5">Análisis únicos</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-xl text-center">
                  <p className="text-xl font-bold text-amber-700">{sharingStats.usuarios_compartiendo}</p>
                  <p className="text-[11px] text-amber-500 mt-0.5">Usuarios que comparten</p>
                </div>
              </div>
              {sharingStats.por_dia.length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={sharingStats.por_dia}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94A3B8' }} tickLine={false} axisLine={false} interval={Math.max(0, Math.floor(sharingStats.por_dia.length / 8))} />
                    <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '12px' }} cursor={{ fill: 'rgba(79, 70, 229, 0.06)' }} />
                    <Bar dataKey="compartidos" fill="#6366F1" radius={[4, 4, 0, 0]} name="Compartidos" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-xs text-gray-400 text-center py-6">No hay actividad de compartir en este periodo</p>
              )}
            </div>
          )}

          {/* Tables row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-100 p-5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <SectionHeader
                title="Sesiones por usuario"
                description="Ranking de usuarios por sesiones creadas y mensajes enviados. Identifica los usuarios más activos de la plataforma."
              />
              <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 text-xs font-medium text-gray-500">Usuario</th>
                      <th className="text-right py-2 text-xs font-medium text-gray-500">Sesiones</th>
                      <th className="text-right py-2 text-xs font-medium text-gray-500">Mensajes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessionsPerUser.length === 0 ? (
                      <tr><td colSpan={3} className="text-center py-8 text-gray-400 text-xs">Sin datos</td></tr>
                    ) : sessionsPerUser.map(u => (
                      <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                        <td className="py-2.5">
                          <p className="font-medium text-gray-900 text-xs">{u.nombre}</p>
                          <p className="text-[10px] text-gray-400">{u.email}</p>
                        </td>
                        <td className="text-right font-mono text-xs text-gray-700">{u.sesiones}</td>
                        <td className="text-right font-mono text-xs text-gray-700">{u.total_mensajes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <SectionHeader
                title="Consumo de tokens por usuario"
                description="Tokens de entrada y salida por usuario. Monitorea quién consume más recursos del modelo de IA."
              />
              <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 text-xs font-medium text-gray-500">Usuario</th>
                      <th className="text-right py-2 text-xs font-medium text-gray-500">Entrada</th>
                      <th className="text-right py-2 text-xs font-medium text-gray-500">Salida</th>
                      <th className="text-right py-2 text-xs font-medium text-gray-500">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tokensPerUser.length === 0 ? (
                      <tr><td colSpan={4} className="text-center py-8 text-gray-400 text-xs">Sin datos</td></tr>
                    ) : tokensPerUser.map(u => (
                      <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                        <td className="py-2.5">
                          <p className="font-medium text-gray-900 text-xs">{u.nombre}</p>
                          <p className="text-[10px] text-gray-400">{u.email}</p>
                        </td>
                        <td className="text-right font-mono text-xs text-gray-700">{formatNumber(u.tokens_entrada)}</td>
                        <td className="text-right font-mono text-xs text-gray-700">{formatNumber(u.tokens_salida)}</td>
                        <td className="text-right font-mono text-xs font-semibold" style={{ color: '#4F46E5' }}>{formatNumber(u.tokens_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Recent activity */}
          <div className="bg-white rounded-xl border border-gray-100 p-5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <SectionHeader
              title="Actividad reciente global"
              description="Registro en tiempo real de las acciones realizadas por todos los usuarios: consultas, descargas, compartidos, etc."
            />
            {recentActivity.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">Sin actividad registrada</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[340px] overflow-y-auto">
                {recentActivity.map(a => {
                  const Icon = ACTIVITY_ICONS[a.type] || Activity;
                  return (
                    <div key={a.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-gray-50">
                        <Icon className="w-4 h-4 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-700">
                          <span className="font-medium">{a.user}</span>
                          {' '}{a.description}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{formatTime(a.timestamp)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
